import { Button, LinearProgress, Typography } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Grid from '@mui/material/Unstable_Grid2';
import MyAppBar from 'components/AppBar/AppBar';
import ErrorView from 'components/Error/ErrorView';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestExternalData, useFetch } from 'services/APIClient';
import {
  DELIVERECT_ACCOUNT_INFO_URL,
  USER_DEVICES_URL,
} from 'services/APIConstants';
import Interceptor from 'services/Interceptor';
import { AuthToken, isLoggedIn } from 'services/Token';

const StoreSelector = () => {
  const [selectedOptionValue, setSelectedOptionValue] = useState('');
  const [deliverectOptions, setDeliverectOptions] = useState([]);
  const [isFetchingDeliverect, setIsFetchingDeliverect] = useState(true);
  const { isFetching, data, error } = useFetch(
    USER_DEVICES_URL,
    AuthToken(),
    isLoggedIn() ? Interceptor : null
  );
  const navigate = useNavigate();

  useEffect(() => {
    let ignore = false;

    const loadDeliverectAccounts = async () => {
      setIsFetchingDeliverect(true);
      try {
        const payload = await requestExternalData(
          DELIVERECT_ACCOUNT_INFO_URL(),
          AuthToken(),
          isLoggedIn() ? Interceptor : null
        );
        const options = (payload?.results || [])
          .map((entry) => ({
            type: 'deliverect',
            accountId: String(
              entry?.id ||
                entry?.account_id ||
                entry?.deliverect_account?.id ||
                ''
            ),
            posUuid: String(entry?.pos_uuid || ''),
            locationName:
              entry?.location_name ||
              entry?.deliverect_account?.location_name ||
              entry?.location_id ||
              entry?.deliverect_account?.location_id ||
              'Unknown Deliverect Location',
          }))
          .filter(
            (entry) => Boolean(entry.accountId) && Boolean(entry.posUuid)
          );

        if (!ignore) {
          setDeliverectOptions(options);
        }
      } catch (err) {
        if (!ignore) {
          setDeliverectOptions([]);
        }
      } finally {
        if (!ignore) {
          setIsFetchingDeliverect(false);
        }
      }
    };

    loadDeliverectAccounts();

    return () => {
      ignore = true;
    };
  }, []);

  const posOptions = useMemo(
    () =>
      (data?.results || []).map((pos) => ({
        type: 'pos',
        posUuid: pos.uuid,
        label: pos.machine_name,
        value: `pos:${pos.uuid}`,
      })),
    [data]
  );

  const combinedOptions = useMemo(
    () => [
      ...posOptions,
      ...deliverectOptions.map((entry) => ({
        type: 'deliverect',
        posUuid: entry.posUuid,
        accountId: entry.accountId,
        label: `Deliverect - ${entry.locationName}`,
        value: `deliverect:${entry.accountId}:${entry.posUuid}`,
      })),
    ],
    [posOptions, deliverectOptions]
  );

  const selectedOption = useMemo(
    () =>
      combinedOptions.find((option) => option.value === selectedOptionValue),
    [combinedOptions, selectedOptionValue]
  );

  if (isFetching || isFetchingDeliverect) {
    return (
      <Typography variant="h6" align="top" mt={'0%'}>
        <LinearProgress />
      </Typography>
    );
  }

  if (error) {
    return <ErrorView statusCode={error.code} />;
  }

  const savePosData = (uuid) => {
    let posData = {};
    for (let i = 0; i < data.results.length; i++) {
      posData[data.results[i].uuid] = data.results[i].machine_name;
      if (uuid === data.results[i].uuid) {
        posData.tax1_rate = data.results[i].tax1_rate || 0;
        posData.tax2_rate = data.results[i].tax2_rate || 0;
        posData.tax3_rate = data.results[i].tax3_rate || 0;
      }
    }
    posData.current = uuid;
    localStorage.setItem('selectedPos', JSON.stringify(posData));
  };

  return (
    <>
      <MyAppBar isMain={true} />
      <Grid
        container
        alignItem="center"
        justifyContent="center"
        sx={{ marginTop: '100px' }}
      >
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <FormControl
            sx={{
              backgroundColor: 'white',
              color: 'black',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '50%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <FormLabel sx={{ textAlign: 'center', marginBottom: '15px' }}>
              Select POS Or Deliverect Location
            </FormLabel>

            {/* Scrollable container for radio buttons */}
            <Box
              sx={{
                overflowY: 'auto',
                maxHeight: '50vh',
                marginBottom: '15px',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#555',
                },
              }}
            >
              <RadioGroup
                name="store-selector-radio-group"
                sx={{ textAlign: 'center' }}
                value={selectedOptionValue}
                onChange={(event) => setSelectedOptionValue(event.target.value)}
              >
                {combinedOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </Box>

            <Button
              variant="outlined"
              onClick={() => {
                if (!selectedOption) {
                  return;
                }

                if (selectedOption.type === 'deliverect') {
                  savePosData(selectedOption.posUuid);
                  navigate(
                    '/stores/deliverect/' +
                      selectedOption.posUuid +
                      '/' +
                      selectedOption.accountId
                  );
                  return;
                }

                savePosData(selectedOption.posUuid);
                navigate('/stores/' + selectedOption.posUuid);
              }}
              disabled={!selectedOption}
              sx={{ marginBottom: '8px' }}
            >
              {selectedOption?.type === 'deliverect'
                ? 'Proceed to Deliverect Store'
                : 'Proceed'}
            </Button>
            <Typography variant="caption">
              NOTE: Select one option and proceed
            </Typography>
          </FormControl>
        </Backdrop>
      </Grid>
    </>
  );
};

export default StoreSelector;
